import React, { useMemo, useState } from 'react';
import {
  findDaypartConflict,
  formatDaypartHour,
  formatDaypartRange,
  useAdminDayparts
} from '../../hooks/useAdminDayparts.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const DEFAULT_RECORD = {
  nombre: '',
  hora_inicio: 6,
  hora_fin: 12,
  activo: true,
  sort_order: 0
};

const DaypartsSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveDaypart, deleteDaypart } = useAdminDayparts();
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items, loading, editorState.open]);

  const activeCount = useMemo(() => items.filter((item) => item.activo).length, [items]);
  const overnightCount = useMemo(() => (
    items.filter((item) => Number(item.hora_fin) <= Number(item.hora_inicio)).length
  ), [items]);

  const handleOpenEditor = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record ? { ...record } : { ...DEFAULT_RECORD, sort_order: items.length + 1 }
    });
  };

  const handleSave = async (values) => {
    const conflict = findDaypartConflict(values, items);
    if (conflict) {
      onNotify?.({ type: 'warning', message: conflict });
      return;
    }
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveDaypart(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Daypart actualizado correctamente.' : 'Daypart creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el daypart.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar el daypart "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteDaypart(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Daypart eliminado.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el daypart.' });
    }
  };

  const closeEditor = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="dayparts-section" className="admin-section active">
      <div className="section-header">
        <div className="section-title-group">
          <div className="section-eyebrow">Pronósticos</div>
          <h2 className="section-title">Dayparts</h2>
          <p className="section-subtitle">
            Define rangos horarios globales para organizar futuras generaciones de pronóstico.
          </p>
        </div>
        <div className="section-actions">
          <button className="btn-ghost" type="button" onClick={refresh} disabled={loading || editorState.saving}>
            <span className="btn-icon"><i data-lucide="refresh-cw" /></span>
            {loading ? 'Cargando...' : 'Recargar'}
          </button>
          <button className="btn-primary" type="button" onClick={() => handleOpenEditor()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nuevo Daypart
          </button>
        </div>
      </div>

      <div className="dashboard-card full-width">
        <div className="card-header">
          <h3 className="card-title">
            <i data-lucide="clock-7" />
            Resumen
          </h3>
        </div>
        <div className="card-content usage-summary-grid">
          <div className="usage-summary-card">
            <div className="summary-icon"><i data-lucide="layers" /></div>
            <div className="summary-label">Dayparts activos</div>
            <div className="summary-value">{activeCount}</div>
            <div className="summary-hint">Disponibles para consumo futuro</div>
          </div>
          <div className="usage-summary-card">
            <div className="summary-icon"><i data-lucide="moon" /></div>
            <div className="summary-label">Cruzan medianoche</div>
            <div className="summary-value">{overnightCount}</div>
            <div className="summary-hint">Rangos con cierre al día siguiente</div>
          </div>
          <div className="usage-summary-card">
            <div className="summary-icon"><i data-lucide="shield-check" /></div>
            <div className="summary-label">Validación</div>
            <div className="summary-value">Sin solape</div>
            <div className="summary-hint">Intervalos semiabiertos por hora</div>
          </div>
        </div>
      </div>

      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando dayparts...</div>
          </div>
        ) : (
          <table className="data-table" id="dayparts-table">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Nombre</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Rango</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="clock-7" /></div>
                    <div className="empty-state-text">No hay dayparts configurados.</div>
                    <div className="empty-state-subtext">Crea el primer rango horario para comenzar.</div>
                  </td>
                </tr>
              ) : (
                items.map((daypart) => {
                  const crossesMidnight = Number(daypart.hora_fin) <= Number(daypart.hora_inicio);
                  return (
                    <tr key={daypart.id}>
                      <td>{daypart.sort_order ?? 0}</td>
                      <td><strong>{daypart.nombre}</strong></td>
                      <td>{formatDaypartHour(daypart.hora_inicio)}</td>
                      <td>{formatDaypartHour(daypart.hora_fin)}</td>
                      <td>
                        <span className={`status-badge ${crossesMidnight ? 'status-neutral' : 'status-active'}`}>
                          {formatDaypartRange(daypart)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${daypart.activo ? 'status-active' : 'status-inactive'}`}>
                          {daypart.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-btn edit"
                            onClick={() => handleOpenEditor(daypart)}
                            title="Editar"
                          >
                            <i data-lucide="edit" />
                          </button>
                          <button
                            type="button"
                            className="action-btn delete"
                            onClick={() => handleDelete(daypart)}
                            title="Eliminar"
                          >
                            <i data-lucide="trash-2" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {editorState.open ? (
        <DaypartEditorModal
          state={editorState}
          items={items}
          onClose={closeEditor}
          onSubmit={handleSave}
        />
      ) : null}
    </section>
  );
};

const DaypartEditorModal = ({ state, items, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(state.record || DEFAULT_RECORD);

  const conflict = useMemo(() => findDaypartConflict(formValues, items), [formValues, items]);
  const crossesMidnight = Number(formValues.hora_fin) <= Number(formValues.hora_inicio);
  const isDisabled = state.saving || Boolean(conflict) || !(formValues.nombre || '').trim();

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (conflict) {
      return;
    }
    onSubmit({ ...formValues });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h3>{formValues.id ? 'Editar Daypart' : 'Nuevo Daypart'}</h3>
          <button className="modal-close" type="button" onClick={onClose} disabled={state.saving}>
            &times;
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group full-width">
            <label className="form-label">Nombre</label>
            <input
              className="form-input"
              name="nombre"
              value={formValues.nombre || ''}
              onChange={handleChange}
              maxLength={120}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Hora inicio</label>
            <input
              className="form-input"
              name="hora_inicio"
              type="number"
              min="0"
              max="23"
              step="1"
              value={formValues.hora_inicio ?? 0}
              onChange={handleChange}
              required
            />
            <p className="helper-text">{formatDaypartHour(formValues.hora_inicio)}</p>
          </div>
          <div className="form-group">
            <label className="form-label">Hora fin</label>
            <input
              className="form-input"
              name="hora_fin"
              type="number"
              min="0"
              max="23"
              step="1"
              value={formValues.hora_fin ?? 0}
              onChange={handleChange}
              required
            />
            <p className="helper-text">{formatDaypartHour(formValues.hora_fin)}</p>
          </div>
          <div className="form-group">
            <label className="form-label">Orden</label>
            <input
              className="form-input"
              name="sort_order"
              type="number"
              step="1"
              value={formValues.sort_order ?? 0}
              onChange={handleChange}
            />
          </div>
          <div className="form-group checkbox-group">
            <label className="form-label">Estado</label>
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={Boolean(formValues.activo)}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Daypart activo</span>
            </label>
          </div>
          <div className="form-group full-width">
            <div className={`status-badge ${crossesMidnight ? 'status-neutral' : 'status-active'}`}>
              {formatDaypartRange(formValues)}
            </div>
            <p className="helper-text">
              {crossesMidnight
                ? 'Este rango termina al día siguiente y se valida contra ambos lados de medianoche.'
                : 'Este rango se valida como intervalo semiabierto; la hora final no se incluye.'}
            </p>
            {conflict ? <div className="form-error" role="alert">{conflict}</div> : null}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={isDisabled}>
              {state.saving ? 'Guardando...' : 'Guardar Daypart'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DaypartsSection;
